<template>
  <v-app-bar
    app
    color="white"
    clipped-right
    clipped-left
    elevation="1"
    height="64"
    class="px-2"
  >
    <!-- Botón Menú Lateral (Edición) -->
    <v-btn
      v-if="editing"
      icon
      class="mr-2"
      color="primary"
      @click="toggleLeftMenu"
    >
      <v-icon>mdi-menu</v-icon>
    </v-btn>

    <!-- Logo de la aplicación -->
    <div class="d-flex align-center cursor-pointer" @click="goToPage('/')">
      <v-img
        contain
        max-height="36"
        max-width="120"
        src="@/assets/complete_logo.png"
        class="transition-swing"
      />
    </div>

    <!-- Controles de Edición (Solo Desarrollador) -->
    <template v-if="isDeveloper()">
      <v-divider vertical class="mx-3 my-3" />
      <v-btn
        small
        rounded
        depressed
        :color="editing ? 'warning' : 'primary'"
        class="mr-2"
        @click="updateEditing"
      >
        <v-icon left small>
          {{ editing ? 'mdi-pause-circle-outline' : 'mdi-pencil-outline' }}
        </v-icon>
        {{ editing ? 'En Edición' : 'Editar' }}
      </v-btn>

      <v-btn
        v-if="editing"
        small
        rounded
        depressed
        color="success"
        @click="save()"
      >
        <v-icon left small>mdi-content-save-outline</v-icon>
        Guardar
      </v-btn>
    </template>

    <v-spacer></v-spacer>

    <!-- ============================================== -->
    <!--          VISTA ESCRITORIO (MD Y SUPERIOR)      -->
    <!-- ============================================== -->
    <template v-if="$vuetify.breakpoint.mdAndUp">
      <div class="d-flex align-center">

        <!-- GRUPO 1: VISTAS Y NAVEGACIÓN DE PLANTA -->
        <v-tooltip bottom v-if="has_history">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="goToPage('history')" class="mx-1">
              <v-icon color="grey darken-2">mdi-history</v-icon>
            </v-btn>
          </template>
          <span>Histórico</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="goToPage('armarios')" class="mx-1">
              <v-icon color="grey darken-2">mdi-wardrobe-outline</v-icon>
            </v-btn>
          </template>
          <span>Armarios</span>
        </v-tooltip>

        <v-tooltip v-if="isRamos() || isDeveloper()" bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="goToPage('tanques')" class="mx-1">
              <v-icon color="grey darken-2">mdi-gas-cylinder</v-icon>
            </v-btn>
          </template>
          <span>Tanques</span>
        </v-tooltip>

        <v-divider vertical class="mx-2 my-3" />

        <!-- GRUPO 2: GESTIÓN DE ALARMAS -->
        <!-- Reseteo rápido -->
        <v-tooltip bottom v-if="connection">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="resetAlarms" class="mx-1" color="amber darken-3">
              <v-icon>mdi-sync-alert</v-icon>
            </v-btn>
          </template>
          <span>Resetear Alarmas</span>
        </v-tooltip>

        <!-- Navegar a Alarmas -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="goToPage('alarms')" class="mx-1">
              <v-icon color="error">mdi-alert-outline</v-icon>
            </v-btn>
          </template>
          <span>Panel de Alarmas</span>
        </v-tooltip>

        <!-- Filtro / Toggle Alarmas por PLC -->
        <v-menu
          offset-y
          :close-on-content-click="false"
          v-if="isDeveloper() || isRamos()"
          transition="slide-y-transition"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on: tooltipOn }">
                <v-btn
                  icon
                  v-bind="attrs"
                  v-on="{ ...on, ...tooltipOn }"
                  class="mx-1"
                >
                  <v-icon color="grey darken-2">mdi-bell-cog-outline</v-icon>
                </v-btn>
              </template>
              <span>Activar/Desactivar alarmas por PLC</span>
            </v-tooltip>
          </template>

          <v-card width="280">
            <v-card-title class="subtitle-2 font-weight-bold grey lighten-4 py-2">
              Alarmas por PLC
            </v-card-title>
            <v-divider />
            <v-list dense max-height="250" class="overflow-y-auto py-0">
              <v-list-item v-for="plc in alarmas_activas" :key="plc.plc_id" class="px-3">
                <v-list-item-action class="mr-3">
                  <v-checkbox
                    v-model="plc.all_active"
                    color="primary"
                    dense
                    @change="togglePlcAlarm(plc.plc_id, plc.all_active, plc.alarms)"
                  ></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="body-2">PLC {{ plc.plc_name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>

        <v-divider vertical class="mx-2 my-3" />

        <!-- GRUPO 3: MENÚ DE USUARIO Y CONFIGURACIÓN DEL SISTEMA -->
        <v-menu offset-y transition="slide-y-transition" left>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              text
              rounded
              class="px-2 ml-1"
              v-bind="attrs"
              v-on="on"
            >
              <v-avatar size="32" color="primary lighten-4" class="mr-2">
                <v-icon color="primary" small>mdi-account</v-icon>
              </v-avatar>
              <span class="text-caption font-weight-bold text-uppercase d-none d-lg-inline">
                {{ user ? (user.username || user.name || 'Usuario') : 'Cuenta' }}
              </span>
              <v-icon small right color="grey">mdi-chevron-down</v-icon>
            </v-btn>
          </template>

          <v-list dense width="230">
            <!-- Funciones de Desarrollador / Admin -->
            <template v-if="isDeveloper() || isAdmin()">
              <v-subheader class="text-uppercase text-caption font-weight-bold grey--text">
                Administración
              </v-subheader>

              <v-list-item v-if="isDeveloper()" @click="goToPage('vars')">
                <v-list-item-icon class="mr-3">
                  <v-icon small color="blue-grey">mdi-application-variable</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Variables</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="isDeveloper()" @click="openBackupDialog">
                <v-list-item-icon class="mr-3">
                  <v-icon small color="blue-grey">mdi-cloud-upload-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Copia de seguridad</v-list-item-title>
              </v-list-item>

              <v-list-item @click="createUser()">
                <v-list-item-icon class="mr-3">
                  <v-icon small color="blue-grey">mdi-account-multiple-plus-outline</v-icon>
                </v-list-item-icon>
                <v-list-item-title>Gestionar usuarios</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1" />
            </template>

            <!-- Opciones de Cuenta General -->
            <v-list-item v-if="!isProduction()" @click="passwordDialog = true">
              <v-list-item-icon class="mr-3">
                <v-icon small color="grey darken-2">mdi-key-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Cambiar contraseña</v-list-item-title>
            </v-list-item>

            <v-list-item v-if="!isProduction()" @click="logoutFromApp" class="error--text">
              <v-list-item-icon class="mr-3">
                <v-icon small color="error">mdi-logout-variant</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Cerrar sesión</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

      </div>
    </template>

    <!-- ============================================== -->
    <!--         VISTA MÓVIL (PANTALLAS PEQUEÑAS)       -->
    <!-- ============================================== -->
    <template v-else>
      <v-menu offset-y left transition="slide-y-transition">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on">
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense width="230">
          <v-subheader class="text-caption text-uppercase font-weight-bold">Navegación</v-subheader>
          
          <v-list-item v-if="!isLaboratory() && has_history" @click="goToPage('history')">
            <v-list-item-icon><v-icon small>mdi-history</v-icon></v-list-item-icon>
            <v-list-item-title>Historial</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="!isLaboratory()" @click="goToPage('alarms')">
            <v-list-item-icon><v-icon small color="error">mdi-alert-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Alarmas</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="!isLaboratory() && connection" @click="resetAlarms">
            <v-list-item-icon><v-icon small color="warning">mdi-sync-alert</v-icon></v-list-item-icon>
            <v-list-item-title>Reiniciar alarmas</v-list-item-title>
          </v-list-item>

          <v-list-item @click="goToPage('armarios')">
            <v-list-item-icon><v-icon small>mdi-wardrobe-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Armarios</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="isRamos() || isDeveloper()" @click="goToPage('tanques')">
            <v-list-item-icon><v-icon small>mdi-gas-cylinder</v-icon></v-list-item-icon>
            <v-list-item-title>Tanques</v-list-item-title>
          </v-list-item>

          <v-divider class="my-1" />
          <v-subheader class="text-caption text-uppercase font-weight-bold">Ajustes</v-subheader>

          <v-list-item v-if="isDeveloper()" @click="openBackupDialog">
            <v-list-item-icon><v-icon small>mdi-cloud-upload-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Copia de seguridad</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="isDeveloper() || isAdmin()" @click="createUser">
            <v-list-item-icon><v-icon small>mdi-account-multiple-plus-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Usuarios</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="!isProduction()" @click="passwordDialog = true">
            <v-list-item-icon><v-icon small>mdi-key-outline</v-icon></v-list-item-icon>
            <v-list-item-title>Cambiar contraseña</v-list-item-title>
          </v-list-item>

          <v-list-item v-if="!isProduction()" @click="logoutFromApp" class="error--text">
            <v-list-item-icon><v-icon small color="error">mdi-logout-variant</v-icon></v-list-item-icon>
            <v-list-item-title>Cerrar sesión</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>

    <!-- Diálogo: Guardar Copia de Seguridad -->
    <v-dialog v-model="dialog" persistent max-width="450px">
      <v-card class="rounded-lg">
        <v-card-title class="headline grey lighten-4">
          <v-icon left color="primary">mdi-cloud-upload</v-icon>
          Copia de seguridad
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model="backupFileName"
            label="Nombre del archivo"
            outlined
            dense
            placeholder="Ej: backup_planta_2025"
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" depressed @click="makeBackup">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo: Alerta de Guardado de Datos -->
    <v-dialog
      v-model="finish_dialog"
      max-width="450px"
      transition="dialog-transition"
    >
      <v-alert
        :type="alertType"
        colored-border
        elevation="2"
        class="mb-0"
      >
        <div class="d-flex align-center justify-space-between">
          <span>{{ error_text }}</span>
          <v-btn text small @click="finish_dialog = false">Cerrar</v-btn>
        </div>
      </v-alert>
    </v-dialog>

    <!-- Diálogo: Cambio de Contraseña -->
    <v-dialog
      v-model="passwordDialog"
      max-width="450px"
      transition="dialog-transition"
    >
      <v-card class="rounded-lg">
        <v-card-title class="headline grey lighten-4">
          <v-icon left color="primary">mdi-lock-reset</v-icon>
          Cambiar contraseña
        </v-card-title>

        <v-card-text class="pt-4">
          <v-text-field
            v-model="first_password"
            label="Nueva contraseña"
            type="password"
            outlined
            dense
            required
          ></v-text-field>
          <v-text-field
            v-model="second_password"
            label="Confirmar contraseña"
            type="password"
            outlined
            dense
            required
          ></v-text-field>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="passwordDialog = false">Cancelar</v-btn>
          <v-btn color="primary" depressed @click="changePassword">Actualizar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app-bar>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  data() {
    return {
      dialog: false, // Controla el diálogo
      backupFileName: "", // Almacena el nombre del archivo

      passwordDialog: false,
      first_password: "",
      second_password: "",
       
      //Dialog de confirmacion de guardado
      finish_dialog: false,
      error_text: "",
      alertType: "",

      alarmas_activas: [],
    };
  },
  async mounted(){
    await this.getAlarmStatus();
  },
  computed: {
    ...mapState(["editing", "pages", "originalPages", "selectedPage", "user"]),

    connection() {
      return this.actualPage()?.global_item_list.find((gi) => gi.settings.component_type == "koiote-snap7-connection");
    },

    has_history() {
      return (
        this.$route.path == "/history" ||
        this.actualPage()
          ?.global_item_list.find((gi) => gi.settings.component_type.includes("connection"))
          ?.customized?.vartable?.some((v) => v?.seconds > 0)
      );
    },

    writeTopic() {
      return this.connection?.customized.topic_write;
    },
  },

  methods: {
    ...mapGetters(["actualPage", "isDeveloper", "isLaboratory", "isProduction", "isRamos", "isAdmin"]),
    ...mapActions(["updateEditing", "changeShowLeftMenu", "logout", "setSelectedPageToHome", "setSelectedPageToBack", "postData", "loadPages", "getDataFlask", "getData", "putData"]),
    ...mapActions("nodered", ["doPublish"]),

    toggleLeftMenu() {
      this.changeShowLeftMenu();
    },

    async getAlarmStatus(){
      try{
        const {activas} = await this.getData({ path: "alarms/alarm-status" });
        this.alarmas_activas = activas;
      } catch(e){
        console.error("Error al recoger alarmas", e);
      }
    },

    togglePlcAlarm(plcId, isActive, alarms) {
      // Aquí puedes enviar la actualización al backend
      try{
        console.log(`PLC ${plcId} alarmas: ${isActive ? 'activadas' : 'desactivadas'}`);
  
        this.postData({
          path: `alarms/active-alarma`,
          body: { active: isActive, alarms: alarms }
        });
      } catch(e) {
        console.error("Error al activas/desactivar alarmas");
      }
    },

    orderObjectByKey(unordered) {
      return Object.keys(unordered)
        .sort()
        .reduce((obj, key) => {
          obj[key] = unordered[key];
          return obj;
        }, {});
    },

    async save() {
      try {
        // Detectar páginas modificadas
        const pagesToStore = this.pages
          .map(page => {
            const pageToStore = JSON.parse(JSON.stringify(page));
            pageToStore?.global_item_list
              ?.filter((gi) => gi.settings.component_type.includes("connection"))
              .forEach((c) => {
                (c.customized?.vartable || []).forEach((v) => {
                  v.value = "";
                });
              });
            return pageToStore;
          });

        // Comparar con las originales para saber cuáles han cambiado
        const modifiedPages = pagesToStore.filter(page => {
          const originalIndex = this.originalPages.findIndex(orig => orig._id === page._id);
          if (originalIndex === -1) {
            // Es una página nueva
            return true;
          }

          // Comparar contenido
          const original = this.originalPages[originalIndex];
          return JSON.stringify(this.orderObjectByKey(original)) !== JSON.stringify(this.orderObjectByKey(page));
        });

        // Si no hay páginas modificadas, no hacer nada
        if (modifiedPages.length === 0) {
          console.log("No hay páginas modificadas.");
          this.finish_dialog = true;
          this.error_text = "No hay cambios que guardar";
          this.alertType = "info";
          return;
        }

        // Enviar solo las páginas modificadas
        const response = await this.postData({ path: "pages", body: modifiedPages });

        if (response.ok) {
          alert('Guardado correctamente.');
        } else {
          alert('Error al guardar.');
        }

        // Actualizar el estado original con las nuevas páginas
        this.originalPages = JSON.parse(JSON.stringify(pagesToStore));

        this.loadPages(); // Opcional: recargar para asegurar consistencia

        // Desplegar diálogo correcto
        // this.finish_dialog = true;
        // this.error_text = "Datos guardados correctamente";
        // this.alertType = "success";
      } catch (e) {
        console.error("Error al guardar páginas", e);

        // Desplegar diálogo de error
        // this.finish_dialog = true;
        // this.error_text = "Error al guardar datos";
        // this.alertType = "error";
      }
    },

    //enviar a la pagina de registro de usuario
    createUser() {
      this.$router.push(`/register`);
    },

    resetAlarms() {
      const formattedResetAddress = this.connection.customized.vartable.find(
        (v) => v.formatted_address == this.connection.customized.reset_address
      )?.address;

      this.doPublish({
        topic: this.writeTopic,
        message: {
          address: formattedResetAddress,
          value: true,
        },
        description: "Alarmas reseteadas",
      });
    },

    goToPage(goToPageName) {
      if (goToPageName === "/") {
        if (this.$route.path === "/") {
          this.setSelectedPageToHome();
        } else {
          this.setSelectedPageToBack();
          this.$router.push({ path: "/" });
        }
      } else {
        // Para otras rutas (ej: "history", "alarms", etc.)
        let targetPath = `/${goToPageName}`;
        if (goToPageName === "history") {
          targetPath = `/history/${this.selectedPage}`;
        }

        // Solo navega si no estás ya en una ruta que incluya goToPageName
        if (!this.$route.path.includes(goToPageName)) {
          this.$router.push({ path: targetPath });
        }
      }
    },

    async changePassword(){
      if(this.first_password == this.second_password){
        try{
          const body = {
            new_password: this.first_password,
            user: this.user
          }
          await this.putData({
            path: `auth/password`,
            body: body,
          });
          // Desplegar diálogo correcto
          this.finish_dialog = true;
          this.error_text = "Contraseña cambiada correctamente";
          this.alertType = "success";
        } catch(e){
          console.error("Error al cambiar contraseña", e);

          // Desplegar diálogo de error
          this.finish_dialog = true;
          this.error_text = "Error al cambiar contraseña";
          this.alertType = "error";
        }

      } else{
        alert('Las contraseñas deben coincidir.');
      }
    },

    logoutFromApp() {
      this.logout();
      location.reload(true);
      this.$router.push(`login`).catch(() => {}); //Si hay cambios sin guardar se cancela el cambio de ruta
    },

    // Abre el diálogo para pedir el nombre del archivo
    openBackupDialog() {
      this.dialog = true;
    },
    
    // Cierra el diálogo sin hacer nada
    closeDialog() {
      this.dialog = false;
    },

    // Llama al endpoint para hacer la copia de seguridad
    async makeBackup() {
      try {
        const payload = {name: this.backupFileName}
        const response = await this.postData({ path: "backups", body:  payload});
        console.log('respuesta del backup',response);
        if (response.ok) {
          alert('Copia de seguridad realizada con éxito.');
        } else {
          alert('Error al realizar la copia de seguridad.');
        }
      } catch (error) {
        alert('Error en la conexión: ' + error.message);
      }

      this.closeDialog();
    },
  },
  watch:{
    has_history(val){
      console.log("has history",val);
    }
  }
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
